import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { useActiveAboutVideos, type AboutVideo } from "@/hooks/useAboutVideos";
import { useContentHover } from '@/hooks/useContentHover';
import { useSectionVisibility } from '@/hooks/useSectionVisibility';
import { mergeRefs } from '@/utils/mergeRefs';

function clamp01(n: number) {
  return Math.max(0, Math.min(1, n));
}

function easeInOutSine(t: number) {
  return 0.5 - 0.5 * Math.cos(Math.PI * clamp01(t));
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function setRect(el: SVGRectElement | null, x: number, y: number, w: number, h: number) {
  if (!el) return;
  el.setAttribute("x", String(x));
  el.setAttribute("y", String(y));
  el.setAttribute("width", String(w));
  el.setAttribute("height", String(h));
}

// One-time mobile check to select adaptive video URL
const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;
function resolveVideoUrl(video: AboutVideo): string {
  return (isMobile && video.video_url_mobile) ? video.video_url_mobile : video.video_url;
}

export default function MaskedVideoHero() {
  const { data: videos = [] } = useActiveAboutVideos();

  // ⚠️ ANALYTICS: Hero hover tracking
  const { contentHoverProps: heroHoverProps } = useContentHover({
    contentId: 'about-hero',
    contentType: 'hero',
    contentTitle: 'About Hero',
    contentSection: 'hero',
    contentPage: 'about',
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // ⚠️ ANALYTICS: Section visibility tracking (FIX 8)
  const { ref: sectionRef } = useSectionVisibility({
    sectionId: 'hero',
    sectionName: 'Hero',
    pageName: 'About',
    position: 0,
  });

  // Merge containerRef and sectionRef using shared utility
  const mergedRef = mergeRefs(containerRef, sectionRef);

  // Ping-pong player refs
  const playerARf = useRef<HTMLVideoElement>(null);
  const playerBRf = useRef<HTMLVideoElement>(null);

  // Current active player: 0 = A, 1 = B
  const [activePlayer, setActivePlayer] = useState<0 | 1>(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Timer ref for duration-based switching
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // SVG mask rect refs (two bars)
  const topRectRef = useRef<SVGRectElement>(null);
  const bottomRectRef = useRef<SVGRectElement>(null);

  // Switch to next video
  const switchPlayer = useCallback(() => {
    if (videos.length === 0) return;

    const nextIndex = (currentIndex + 1) % videos.length;
    const nextPlayer = activePlayer === 0 ? playerBRf.current : playerARf.current;

    if (nextPlayer && videos[nextIndex]) {
      nextPlayer.src = resolveVideoUrl(videos[nextIndex]);
      nextPlayer.load();
      nextPlayer.play().catch(() => {});
    }

    setCurrentIndex(nextIndex);
    setActivePlayer((prev) => (prev === 0 ? 1 : 0));
  }, [activePlayer, currentIndex, videos]);

  // Setup duration-based timer when video plays
  const setupDurationTimer = useCallback(
    (video: AboutVideo) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (video.duration_seconds && video.duration_seconds > 0) {
        timerRef.current = setTimeout(() => {
          switchPlayer();
        }, video.duration_seconds * 1000);
      }
    },
    [switchPlayer]
  );

  // Handle video ended (fallback when no duration set)
  const handleVideoEnded = useCallback(() => {
    const currentVideo = videos[currentIndex];
    if (!currentVideo?.duration_seconds) {
      switchPlayer();
    }
  }, [currentIndex, videos, switchPlayer]);

  // Handle video play (setup timer)
  const handleVideoPlay = useCallback(() => {
    const currentVideo = videos[currentIndex];
    if (currentVideo) {
      setupDurationTimer(currentVideo);
    }
  }, [currentIndex, videos, setupDurationTimer]);

  // Initialize first video
  useEffect(() => {
    if (videos.length === 0) return;

    const playerA = playerARf.current;
    if (playerA && videos[0]) {
      playerA.src = resolveVideoUrl(videos[0]);
      playerA.load();
      playerA.play().catch(() => {});
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [videos]);

  // Scroll-driven animation
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  // Animation transforms for text
  const headlineY = useTransform(scrollYProgress, [0, 0.3], [0, 150]);
  const headlineOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  // NOTE: A single CSS `clip-path: polygon(...)` cannot represent two disjoint
  // rectangles. We use an SVG clipPath with two animatable rects instead.

  // Logo SVG original dimensions for aspect ratio
  const LOGO_ASPECT_RATIO = 270 / 183; // width / height = ~1.475
  const TOP_BAR_HEIGHT_RATIO = 65 / 183; // 35.5% of logo height
  const BOTTOM_BAR_HEIGHT_RATIO = 66 / 183; // 36.1% of logo height  
  const GAP_RATIO = 52 / 183; // 28.4% of logo height
  const BOTTOM_WIDTH_RATIO = 210 / 270; // 77.8% of top bar width

  const applyMaskAtProgress = useCallback(
    (raw: number) => {
      const progress = clamp01(raw);

      // Get viewport dimensions
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isMobile = vw < 768;

      // ===== RESPONSIVE LOGO SIZING =====
      // On desktop: use fixed pixel width (456px) capped at 60% of viewport
      // On mobile: use percentage of viewport width (70%) to scale down proportionally
      // This ensures the logo maintains its aspect ratio on all screen sizes
      
      let logoWidthPx: number;
      if (isMobile) {
        // Mobile: use 65% of viewport width for smaller, proportional logo
        logoWidthPx = vw * 0.65;
      } else {
        // Desktop: fixed 456px, capped at 60% of viewport
        logoWidthPx = Math.min(456, vw * 0.60);
      }
      
      // Calculate height from width using aspect ratio (preserves logo shape)
      const logoHeightPx = logoWidthPx / LOGO_ASPECT_RATIO;
      
      // Convert to viewport fractions for objectBoundingBox
      const logoWidthFrac = logoWidthPx / vw;
      const logoHeightFrac = logoHeightPx / vh;

      // Both bars have EQUAL height (use average of the two ratios)
      const avgBarRatio = (TOP_BAR_HEIGHT_RATIO + BOTTOM_BAR_HEIGHT_RATIO) / 2;
      const barHeightFrac = logoHeightFrac * avgBarRatio;
      const gapFrac = logoHeightFrac * GAP_RATIO;
      const totalLogoHeight = barHeightFrac + gapFrac + barHeightFrac;

      // Top bar width and position (centered)
      const topW0 = logoWidthFrac;
      const topX0 = (1 - topW0) / 2;

      // Bottom bar width (narrower, 77.8% of top) and position (centered)
      const bottomW0 = logoWidthFrac * BOTTOM_WIDTH_RATIO;
      const bottomX0 = (1 - bottomW0) / 2;

      // ===== EQUAL SPACING CALCULATION =====
      // Elements from top to bottom:
      // 1. Primary navbar: 44px (mobile) or 44px (lg - the bar itself)
      // 2. Secondary navbar: ~44px (desktop only)
      // 3. [GAP A]
      // 4. Logo
      // 5. [GAP B]
      // 6. Text (~120px including line height)
      // 7. [GAP C]
      // 8. Bottom of viewport
      
      const isLg = window.matchMedia("(min-width: 1024px)").matches;
      const primaryNavPx = 44; // Primary nav height
      const secondaryNavPx = isLg ? 44 : 0; // Secondary nav only on lg+
      const textHeightPx = isLg ? 140 : 100; // Approximate text block height
      
      const fixedElementsHeight = primaryNavPx + secondaryNavPx + (totalLogoHeight * vh) + textHeightPx;
      const availableForGaps = vh - fixedElementsHeight;
      const equalGapPx = Math.max(availableForGaps / 3, 20); // Minimum 20px gap
      const equalGapFrac = equalGapPx / vh;

      // Calculate logo position based on equal spacing
      // Logo top = navbar + secondary nav + gap A
      const logoTopFrac = (primaryNavPx + secondaryNavPx) / vh + equalGapFrac;
      
      const topY0 = logoTopFrac;
      const bottomY0 = topY0 + barHeightFrac + gapFrac;

      // True center of viewport (where logo moves to as text fades)
      const trueCenterY = 0.5;

      // Targets across phases
      const xP1 = 0.05;
      const wP1 = 0.9;
      const meetY = trueCenterY;

      const xP2 = 0.02;
      const wP2 = 0.96;
      const topY2 = 0.05;
      const bottomY2 = 0.95;

      const xP3 = 0;
      const wP3 = 1;
      const topY3 = 0;
      const bottomY3 = 1;

      let topX: number;
      let topY: number;
      let topW: number;
      let topBottom: number;
      let bottomX: number;
      let bottomTop: number;
      let bottomW: number;
      let bottomBottom: number;

      if (progress <= 0.4) {
        const t = easeInOutSine(progress / 0.4);

        // Bars expand and move toward center
        topX = lerp(topX0, xP1, t);
        topW = lerp(topW0, wP1, t);
        topY = lerp(topY0, 0, t);
        topBottom = lerp(topY0 + barHeightFrac, meetY, t);

        bottomX = lerp(bottomX0, xP1, t);
        bottomW = lerp(bottomW0, wP1, t);
        bottomTop = lerp(bottomY0, meetY, t);
        bottomBottom = lerp(bottomY0 + barHeightFrac, 1, t);
      } else if (progress <= 0.7) {
        const t = easeInOutSine((progress - 0.4) / 0.3);

        topX = lerp(xP1, xP2, t);
        topW = lerp(wP1, wP2, t);
        topY = lerp(0, topY2, t);
        topBottom = meetY;

        bottomX = lerp(xP1, xP2, t);
        bottomW = lerp(wP1, wP2, t);
        bottomTop = meetY;
        bottomBottom = lerp(1, bottomY2, t);
      } else {
        const t = easeInOutSine((progress - 0.7) / 0.3);

        topX = lerp(xP2, xP3, t);
        topW = lerp(wP2, wP3, t);
        topY = lerp(topY2, topY3, t);
        topBottom = meetY;

        bottomX = lerp(xP2, xP3, t);
        bottomW = lerp(wP2, wP3, t);
        bottomTop = meetY;
        bottomBottom = lerp(bottomY2, bottomY3, t);
      }

      setRect(topRectRef.current, topX, topY, topW, Math.max(0, topBottom - topY));
      setRect(bottomRectRef.current, bottomX, bottomTop, bottomW, Math.max(0, bottomBottom - bottomTop));
    },
    []
  );

  useMotionValueEvent(scrollYProgress, "change", applyMaskAtProgress);

  // Recalculate on resize to maintain fixed pixel dimensions
  useEffect(() => {
    applyMaskAtProgress(0);
    const handleResize = () => applyMaskAtProgress(scrollYProgress.get());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [applyMaskAtProgress, scrollYProgress]);

  return (
    <div ref={mergedRef} className="relative h-[250vh]">
      <section className="sticky top-0 h-dvh min-h-dvh w-full bg-background overflow-hidden" {...heroHoverProps}>
        {/* SVG clipPath defs (supports multiple disjoint rects cleanly) */}
        <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
          <defs>
            <clipPath id="about-hero-clip" clipPathUnits="objectBoundingBox">
              {/* top bar */}
              <rect ref={topRectRef} x="0" y="0" width="0" height="0" />
              {/* bottom bar */}
              <rect ref={bottomRectRef} x="0" y="0" width="0" height="0" />
            </clipPath>
          </defs>
        </svg>

        {/* Video container with SVG clip-path */}
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            clipPath: "url(#about-hero-clip)",
            WebkitClipPath: "url(#about-hero-clip)",
          }}
        >
          {/* Player A */}
          <video
            ref={playerARf}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-100"
            style={{
              opacity: activePlayer === 0 ? 1 : 0,
              zIndex: activePlayer === 0 ? 1 : 0,
            }}
            poster={videos[0]?.thumbnail_url || undefined}
            muted
            playsInline
            loop={videos.length === 1}
            onEnded={handleVideoEnded}
            onPlay={handleVideoPlay}
          />

          {/* Player B */}
          <video
            ref={playerBRf}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-100"
            style={{
              opacity: activePlayer === 1 ? 1 : 0,
              zIndex: activePlayer === 1 ? 1 : 0,
            }}
            poster={videos.length > 1 ? (videos[1]?.thumbnail_url || undefined) : (videos[0]?.thumbnail_url || undefined)}
            muted
            playsInline
            loop={videos.length === 1}
            onEnded={handleVideoEnded}
            onPlay={handleVideoPlay}
          />
        </div>

        {/* Content overlay */}
        <div className="relative z-10 mx-auto h-full max-w-[1400px] px-4 md:px-8 pointer-events-none">
          <div className="flex h-full flex-col items-center justify-end pt-[44px] lg:pt-[92px] pb-16 md:pb-24">
            {/* Headline */}
            <motion.h1
              className="text-4xl font-bold tracking-tight text-foreground md:text-6xl lg:text-7xl text-center px-4 max-w-4xl"
              style={{ y: headlineY, opacity: headlineOpacity }}
            >
              Making finance personal since 1990.
            </motion.h1>
          </div>
        </div>
      </section>
    </div>
  );
}